import { useEffect, useState } from 'react';
import { Loader2, Save, Sliders } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { CANADIAN_CITIES } from '../lib/constants';

const AFRICAN_COUNTRIES = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Ethiopia', 'Tanzania', 'Uganda',
  'Cameroon', 'Senegal', 'Ivory Coast', 'DR Congo', 'Zimbabwe', 'Rwanda',
  'Somalia', 'Sudan', 'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Mozambique',
  'Angola', 'Mali', 'Burkina Faso', 'Sierra Leone', 'Liberia', 'Eritrea',
  'Botswana', 'Namibia', 'Zambia', 'Madagascar',
];

const RELATIONSHIP_INTENTS = [
  'Serious relationship',
  'Marriage-minded',
  'Friendship leading to relationship',
  'Business & Event Buddies',
];

const GENDER_OPTIONS = [
  { value: '', label: 'Anyone' },
  { value: 'Men', label: 'Men' },
  { value: 'Women', label: 'Women' },
];

const COMMON_LANGUAGES = ['English', 'French', 'Yoruba', 'Igbo', 'Swahili', 'Twi', 'Amharic', 'Arabic'];

export default function Preferences() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api('/users/preferences');
        setPrefs({
          age_min: 18,
          age_max: 50,
          gender_preference: '',
          city: '',
          country_of_origin: '',
          tribe: '',
          religion: '',
          language: '',
          relationship_intent: '',
          ...(data.preferences || {}),
        });
      } catch (err) {
        toast.error(err.message || 'Failed to load preferences');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function update(k, v) { setPrefs({ ...prefs, [k]: v }); }

  async function handleSave() {
    if (prefs.age_min < 18 || prefs.age_max > 99 || prefs.age_min > prefs.age_max) {
      return toast.error('Invalid age range');
    }
    setSaving(true);
    try {
      const data = await api('/users/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          age_min: parseInt(prefs.age_min),
          age_max: parseInt(prefs.age_max),
          gender_preference: prefs.gender_preference || null,
          city: prefs.city || null,
          country_of_origin: prefs.country_of_origin || null,
          tribe: prefs.tribe || null,
          religion: prefs.religion || null,
          language: prefs.language || null,
          relationship_intent: prefs.relationship_intent || null,
        }),
      });
      setPrefs({ ...prefs, ...(data.preferences || {}) });
      toast.success('Preferences saved');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !prefs) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  }

  const fieldCls = 'w-full bg-white border border-[#F4F1DE] rounded-lg px-4 py-3 outline-none focus:border-[#E07A5F] text-[#2B2D42]';
  const labelCls = 'block text-xs font-bold uppercase tracking-wider text-[#6D6875] mb-1.5';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="preferences-page">
      <div className="flex items-center gap-3 mb-2">
        <Sliders className="w-6 h-6 text-[#C8553D]" />
        <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Preferences
        </h1>
      </div>
      <p className="text-sm text-[#6D6875] mb-6">Tailor who you discover. Leave any field blank for "no filter".</p>

      <div className="space-y-5">
        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">Demographics</h2>

          <div className="space-y-4">
            <div>
              <p className={labelCls}>Age range</p>
              <div className="flex items-center gap-3" data-testid="preferences-age-range">
                <input
                  type="number"
                  min="18"
                  max="99"
                  data-testid="preferences-age-min"
                  className={`${fieldCls} w-24 text-center`}
                  value={prefs.age_min}
                  onChange={(e) => update('age_min', e.target.value)}
                />
                <span className="text-[#6D6875]">to</span>
                <input
                  type="number"
                  min="18"
                  max="99"
                  data-testid="preferences-age-max"
                  className={`${fieldCls} w-24 text-center`}
                  value={prefs.age_max}
                  onChange={(e) => update('age_max', e.target.value)}
                />
                <span className="text-sm text-[#B5B5B5]">years old</span>
              </div>
            </div>

            <div>
              <p className={labelCls}>Show me</p>
              <div className="grid grid-cols-3 gap-2" data-testid="preferences-gender-group">
                {GENDER_OPTIONS.map((opt) => {
                  const active = prefs.gender_preference === opt.value;
                  return (
                    <button
                      key={opt.value || 'any'}
                      onClick={() => update('gender_preference', opt.value)}
                      data-testid={`preferences-gender-${(opt.value || 'anyone').toLowerCase()}`}
                      className={`px-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                        active ? 'bg-[#C8553D] border-[#C8553D] text-white' : 'bg-white border-[#F4F1DE] text-[#2B2D42] hover:border-[#E07A5F]'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">Location & Origin</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={labelCls}>City</p>
              <select data-testid="preferences-city-select" className={fieldCls} value={prefs.city} onChange={(e) => update('city', e.target.value)}>
                <option value="">Any</option>
                {CANADIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <p className={labelCls}>Country of origin</p>
              <select data-testid="preferences-country-select" className={fieldCls} value={prefs.country_of_origin} onChange={(e) => update('country_of_origin', e.target.value)}>
                <option value="">Any</option>
                {AFRICAN_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </section>

        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-4">Lifestyle</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={labelCls}>Tribe</p>
              <input data-testid="preferences-tribe-input" className={fieldCls} value={prefs.tribe} onChange={(e) => update('tribe', e.target.value)} placeholder="e.g. Yoruba" />
            </div>
            <div>
              <p className={labelCls}>Religion</p>
              <input data-testid="preferences-religion-input" className={fieldCls} value={prefs.religion} onChange={(e) => update('religion', e.target.value)} placeholder="e.g. Christian" />
            </div>
          </div>

          <div className="mt-4">
            <p className={labelCls}>Language</p>
            <select data-testid="preferences-language-select" className={fieldCls} value={prefs.language} onChange={(e) => update('language', e.target.value)}>
              <option value="">Any</option>
              {COMMON_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div className="mt-4">
            <p className={labelCls}>Looking for</p>
            <select data-testid="preferences-intent-select" className={fieldCls} value={prefs.relationship_intent} onChange={(e) => update('relationship_intent', e.target.value)}>
              <option value="">Any</option>
              {RELATIONSHIP_INTENTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </section>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="preferences-save-button"
          className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Save Preferences</>}
        </button>
      </div>
    </div>
  );
}
